import React, { memo, useCallback, useMemo, useState } from "react";
import "./PatientDeclaration.css";
import { Div, Txt } from "../GenericView";
import { AdlServices } from "../AdlServices";
import { TextareaAutosize } from "@material-ui/core";
import { HomeMedicalDetails, Visit } from "../../@types/PricingModels";
import { c4A4A4A, c7C7C7C, UNDEF } from "../../Constants";
import {
  currently_on_medication,
  current_symptoms,
  current_symptoms_details,
  have_drug_allergy,
  medication_details,
  drug_allergy_details,
  OTHERS,
  home_medical_details,
} from "../../DataConstants";
import { isBlank } from "Util/Utilities";

/**
 * Note: This component directly updates the `homeMedDetails` passed through props
 */

interface PDProps {
  /**
   * List of all available symptoms
   */
  symptomsList: string[];

  /**
   * Home medical details object
   * Note: component will update this directly
   */
  homeMedDetails: HomeMedicalDetails;

  /**
   * Update callback
   */
  onUpdate: (details: HomeMedicalDetails) => void;
}

const PatientDeclaration: React.FC<PDProps> = (props) => {
  //To force re-render
  const [rc, render] = useState(1);

  const { symptomsList, onUpdate } = props;

  const homeMedDetails = props.homeMedDetails || {};
  const selectedSymptoms = homeMedDetails[current_symptoms] || [];
  const otherSymptoms = homeMedDetails[current_symptoms_details];

  const onMedication = homeMedDetails[currently_on_medication];
  const haveAllergies = homeMedDetails[have_drug_allergy];

  const medicationDetails = homeMedDetails[medication_details];
  const allergyDetails = homeMedDetails[drug_allergy_details];

  // calculate the display symptoms only once
  const displaySymptoms = useMemo(
    () =>
      (symptomsList || []).map((s: string) => ({
        name: s,
      })),
    [symptomsList]
  );

  const reRender = useCallback(() => render(rc + 1), [rc]);

  const handleSymptomsChange = useCallback(
    (symptoms: string[]) => {
      const othersVal = homeMedDetails[current_symptoms_details];
      const haveOthers = symptoms.includes(OTHERS);

      if (haveOthers && isBlank(othersVal)) {
        homeMedDetails[current_symptoms] = symptoms.filter((s) => s !== OTHERS);
      } else {
        homeMedDetails[current_symptoms] = symptoms;
      }

      // delete other symptom details when "Others" isn't selected
      !haveOthers && delete homeMedDetails[current_symptoms_details];

      onUpdate(homeMedDetails);
      reRender();
    },
    [homeMedDetails, onUpdate, reRender]
  );

  const onTextChange = useCallback(
    (
      type:
        | "medication_details"
        | "drug_allergy_details"
        | "current_symptoms_details"
    ) => (e: React.ChangeEvent<any>) => {
      homeMedDetails[type] = e.target.value;
      onUpdate(homeMedDetails);
      reRender();
    },
    [homeMedDetails, onUpdate, reRender]
  );

  const yesNoChange = useCallback(
    (
      type: "currently_on_medication" | "have_drug_allergy",
      isYes: boolean
    ) => () => {
      homeMedDetails[type] = isYes;
      onUpdate(homeMedDetails);
      reRender();
    },
    [homeMedDetails, onUpdate, reRender]
  );

  return (
    <Div v color={c4A4A4A} mt={22} ml={2} fs={14}>
      <Txt m>What are the current symptoms?</Txt>
      <Div mt={12} fs={12} className="prd-symptoms">
        <AdlServices
          tdClassName="nvd-service"
          tdInputClassName="prd-add-adl"
          title="+ Add symptom(s)"
          dontDisableDocScroll
          adlList={displaySymptoms}
          requested_services={selectedSymptoms}
          onAdlChange={handleSymptomsChange}
          onOthersChange={onTextChange(current_symptoms_details)}
          othersPlaceholder="Other symptoms"
          othersValue={otherSymptoms}
        />
      </Div>
      <Div mt={30}>
        <Txt m>Is the patient currently on any medication?</Txt>
        <Div className="flex" mt={12}>
          <Txt
            className="prd-chip"
            isSelected={onMedication}
            onClick={yesNoChange(currently_on_medication, true)}
            mr={12}
          >
            Yes
          </Txt>
          <Txt
            className="prd-chip"
            isSelected={onMedication === false}
            onClick={yesNoChange(currently_on_medication, false)}
          >
            No
          </Txt>
        </Div>
      </Div>
      {onMedication && (
        <Div mt={16}>
          <Txt m>Please provide medication details</Txt>
          <Div mt={8} mr={3}>
            <TextareaAutosize
              className="w-full prd-textarea"
              minRows={1}
              placeholder="e.g Ibuprofen - 3 times a day"
              value={medicationDetails}
              onChange={onTextChange(medication_details)}
            />
          </Div>
        </Div>
      )}
      <Div mt={30}>
        <Txt m>Does the patient have any drug allergy?</Txt>
        <Div className="flex" mt={12}>
          <Txt
            className="prd-chip"
            isSelected={haveAllergies}
            onClick={yesNoChange(have_drug_allergy, true)}
            mr={12}
          >
            Yes
          </Txt>
          <Txt
            onClick={yesNoChange(have_drug_allergy, false)}
            isSelected={haveAllergies === false}
            className="prd-chip"
          >
            No
          </Txt>
        </Div>
      </Div>
      {haveAllergies && (
        <Div mt={21}>
          <Txt color={c7C7C7C} fs={12}>
            Please indicate which medication the patient is allergic to, as well
            as the reaction due to the medication
          </Txt>
          <Div mt={8} mr={3}>
            <TextareaAutosize
              className="w-full prd-textarea"
              minRows={3}
              placeholder="e.g Aspirin - Tightening of the airways and throat, causing trouble breathing"
              value={allergyDetails}
              onChange={onTextChange(drug_allergy_details)}
            />
          </Div>
        </Div>
      )}
    </Div>
  );
};

export default memo(PatientDeclaration);

export const havePatientDeclaration = (visit: Visit) => {
  const homeMedDetails = visit[home_medical_details] || {};
  const symptoms = homeMedDetails[current_symptoms];
  const medicationDetails =
    homeMedDetails[currently_on_medication] &&
    homeMedDetails[medication_details];
  const drugAllergyDetails =
    homeMedDetails[have_drug_allergy] && homeMedDetails[drug_allergy_details];
  return !!(!isBlank(symptoms) || medicationDetails || drugAllergyDetails);
};

type PDDProps = {
  visit: Visit;
  className?: string;
};
export const PatientDeclarationDisplay = ({ visit, className }: PDDProps) => {
  const homeMedDetails = visit[home_medical_details] || {};
  const symptoms = homeMedDetails[current_symptoms] || [];
  const symptomsTxt = symptoms
    .join(", ")
    .replace("Others", "Others: " + homeMedDetails[current_symptoms_details]);

  const onMedication = homeMedDetails[currently_on_medication];
  const medicationDetails =
    onMedication === false
      ? "None"
      : onMedication
        ? homeMedDetails[medication_details]
        : "";

  const haveAllergy = homeMedDetails[have_drug_allergy];
  const drugAllergyDetails =
    haveAllergy === false
      ? "None"
      : haveAllergy
        ? homeMedDetails[drug_allergy_details]
        : "";

  /* // testing
  const symptomsTxt = "Fever, headache, Others: Covid";
  const medicationDetails = "Panadol";
  const drugAllergyDetails = "Allergic to aspirin";
  */

  const havePatientDecl = !!(
    symptomsTxt ||
    medicationDetails ||
    drugAllergyDetails
  );

  return !havePatientDecl ? null : (
    <Div className={className} relative>
      {!!symptomsTxt && (
        <span>
          <b>Symptoms:&nbsp;</b>
          {symptomsTxt}
        </span>
      )}
      {!!medicationDetails && (
        <Txt mt={symptomsTxt ? 6 : 0}>
          <b>Current Medication:&nbsp;</b>
          {medicationDetails}
        </Txt>
      )}
      {!!drugAllergyDetails && (
        <Txt mt={symptomsTxt || medicationDetails ? 6 : 0}>
          <b>Drug Allergies:&nbsp;</b>
          {drugAllergyDetails}
        </Txt>
      )}
    </Div>
  );
};

export function validatePatientDeclaration(details: HomeMedicalDetails) {
  const symptoms = details[current_symptoms] || [];
  const symptomDetails = details[current_symptoms_details];
  const onMedication = details[currently_on_medication];
  const medicationDetails = details[medication_details];
  const haveAllergies = details[have_drug_allergy];
  const allergyDetails = details[drug_allergy_details];

  if (
    isBlank(symptoms) ||
    (symptoms.includes(OTHERS) && isBlank(symptomDetails)) ||
    onMedication === UNDEF ||
    (onMedication && isBlank(medicationDetails)) ||
    haveAllergies === UNDEF ||
    (haveAllergies && isBlank(allergyDetails))
  ) {
    return false;
  }
  return true;
}
